import { useDispatch } from 'react-redux'
import { useState } from "react";
import { Image, Button } from "react-bootstrap";
import Logo from "Images/PSOPPC-logo-only.png";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChatDocument from "./ChatDocument";
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { getChatIssueReasons, saveChatFeedback } from './ChatbotAxiosFuncs';
import { STATUSES, ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import ChatIssueReason from './ChatIssueReason';
import { TextButton } from 'Components/Button';
import { HashLink } from "react-router-hash-link";
import { PATHS } from 'Utils/PagePaths';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const ChatMessage = ({
  message,
  resetChatbotChannel,
  chatbotInfoStorage,
  setChatbotInfoStorage
}) => {

  const dispatch = useDispatch();
  const [feedbackReasons, setFeedbackReasons] = useState([]);

  // check the message is question/answer
  const isLeft = (message) => {

    return message?.chatId == null;
  }

  const isFeedback = (message) => {
    // only postive chat id 
    return (message?.chatId != null) && (message?.chatId > 0);
  }

  const isChatIssueReason = (message) => {

    //console.log("isChatIssueReason", message);
    return message?.chatIssueReasons?.length > 0;
  }

  const handleNavigate = (url) => {
    window.open(url, "_blank");
  }

  // build timestamp for message: month day, hour:minute with AM/PM
  const buildTimestamp = () => {
    const now = new Date();
    const month = now.toLocaleString('default', { month: 'short' });
    const day = now.getDate();
    let hour = now.getHours();
    const minute = now.getMinutes().toString().padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12;

    return `${month} ${day}, ${hour}:${minute} ${ampm}`;
  }

  const handleThumbUpByKey = (e, msg) => {
    if ((e.keyCode == 13) && msg) {
      handleThumbUp(msg);
    }
  }

  const handleThumbUp = (msg) => {
    const up = msg.isThumbUp;
    const down = msg.isThumbDown;
    //let msgList = [...chatbotInfo.messageList];
    let msgList = [...chatbotInfoStorage.messageList];
    //if it is either thumbup or thumbdown then return
    if (up || down) {
      return;
    }

    // find the message by chatId in the message list and set thumbup 
    const updatedMsgList = msgList.map(item =>
      item.chatId === msg.chatId ? { ...item, isThumbUp: true } : item
    );

    //console.log("get curMsg in handleThumbUp", updatedMsgList);

    // Ex. body
    // {
    //   chatId: Ex. 20,
    //   isHelpful: Ex. 0=No, 1=Yes,
    //   reasonIds: [] List
    // }

    saveChatFeedback({
      chatId: msg.chatId,
      isHelpful: 1,
      reasonIds: []
    }).then((res) => {
      var statusCode = res.data.status;
      if (statusCode < 0) {
        statusCode = STATUSES.ERROR.code;
      }
      switch (statusCode) {
        case STATUSES.ERROR.code:
          // TODO!!!: how do we handle the global erros in the chat window
          if (res.data.globalErrorMessageList && res.data.globalErrorMessageList.length != 0) {
            setGlobalErrorStatus({
              dispatch: dispatch,
              popupData: {
                visible: true,
                title: ERROR_MESSAGES.GENERAL_ERROR.title,
                msg: res.data.globalErrorMessageList,
                showContactHelp: false
              },
              redirect: {
                redirect: false,
                redirectFirst: false,
                path: "",
                data: {}
              }
            })
          };

          break;
        case STATUSES.SUCCESS.code:
          let question = {
            "message": "This was helpful",
            "chatId": null,
            "documents": [],
            "webpages": [],
            "reset_memory": false,
            "session_id": chatbotInfoStorage.sessionId,
            "timestamp": buildTimestamp()
          }

          let answer = {
            "message": "Thank you for your feedback! 👋",
            "chatId": -1,
            "documents": [],
            "webpages": [],
            "reset_memory": false,
            "session_id": chatbotInfoStorage.sessionId,
            "timestamp": buildTimestamp()
          }

          localStorage.setItem("chatbot_info_storage", JSON.stringify({
            ...chatbotInfoStorage,
            messageList: [
              ...updatedMsgList,
              question,
              answer
            ]
          }));
          setChatbotInfoStorage({
            ...chatbotInfoStorage,
            messageList: [
              ...updatedMsgList,
              question,
              answer
            ]
          });

          resetChatbotChannel.postMessage({
            ...chatbotInfoStorage,
            messageList: [
              ...updatedMsgList,
              question,
              answer
            ]
          });
          break;
      }
    }).catch(
      (error) => {
        //console.log("Error caught while loading data: " + error);
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
      }
    )
  }

  const handleThumbDownByKey = (e, msg) => {
    if ((e.keyCode == 13) && msg) {
      const up = msg.isThumbUp;
      const down = msg.isThumbDown;
      let msgList = [...chatbotInfoStorage.messageList];
      //if it is either thumbup or thumbdown then return
      if (up || down) {
        return;
      }

      // find the message by chatId in the message list and set thumbup 
      const updatedMsgList = msgList.map(item =>
        item.chatId === msg.chatId ? { ...item, isThumbDown: true } : item
      );

      getChatIssueReasons().then((res) => {
        //console.log("getChatIssueReasons", res.data.data);

        let reasons = [];
        let data = res.data.data;

        //add key "checked" for the reasons list item
        for (let i = 0; i < data.length; i++) {

          let r = data[i];
          r["checked"] = false;
          reasons.push(r);
        }

        let question = {
          "message": "This was not helpful",
          "chatId": null,
          "documents": [],
          "webpages": [],
          "reset_memory": false,
          "session_id": chatbotInfoStorage.sessionId,
          "timestamp": buildTimestamp()
        }

        let answer = {
          "message": "Tell us why. Select all that apply. (optional)",
          "chatId": msg.chatId + "_feedback",
          "documents": [],
          "webpages": [],
          "reset_memory": false,
          "session_id": chatbotInfoStorage.sessionId,
          "timestamp": buildTimestamp(),
          "isThumbUp": false,
          "isThumbDown": false,
          "chatIssueReasons": reasons
        }

        localStorage.setItem("chatbot_info_storage", JSON.stringify({
          ...chatbotInfoStorage,
          messageList: [
            ...updatedMsgList,
            question,
            answer
          ]
        }));
        setChatbotInfoStorage({
          ...chatbotInfoStorage,
          messageList: [
            ...updatedMsgList,
            question,
            answer
          ]
        });

        resetChatbotChannel.postMessage({
          ...chatbotInfoStorage,
          messageList: [
            ...updatedMsgList,
            question,
            answer
          ]
        });
      }).catch(
        (error) => {
          //console.log("Error caught while loading data: " + error);
          setGlobalErrorStatus({
            dispatch: dispatch,
            popupData: {
              visible: true,
              title: ERROR_MESSAGES.DATA_ERROR.title,
              msg: ERROR_MESSAGES.DATA_ERROR.message,
              showContactHelp: true
            },
            redirect: {
              redirect: false,
              redirectFirst: false,
              path: "",
              data: {}
            },
            error: error
          });
        }
      );
    }
  }

  const handleThumbDown = (msg) => {
    const up = msg.isThumbUp;
    const down = msg.isThumbDown;
    let msgList = [...chatbotInfoStorage.messageList];
    //if it is either thumbup or thumbdown then return
    if (up || down) {
      return;
    }

    // find the message by chatId in the message list and set thumbup 
    const updatedMsgList = msgList.map(item =>
      item.chatId === msg.chatId ? { ...item, isThumbDown: true } : item
    );

    getChatIssueReasons().then((res) => {
      //console.log("getChatIssueReasons", res.data.data);

      let reasons = [];
      let data = res.data.data;

      //add key "checked" for the reasons list item
      for (let i = 0; i < data.length; i++) {

        let r = data[i];
        r["checked"] = false;
        reasons.push(r);
      }

      let question = {
        "message": "This was not helpful",
        "chatId": null,
        "documents": [],
        "webpages": [],
        "reset_memory": false,
        "session_id": chatbotInfoStorage.sessionId,
        "timestamp": buildTimestamp()
      }

      let answer = {
        "message": "Tell us why. Select all that apply. (optional)",
        "chatId": msg.chatId + "_feedback",
        "documents": [],
        "webpages": [],
        "reset_memory": false,
        "session_id": chatbotInfoStorage.sessionId,
        "timestamp": buildTimestamp(),
        "isThumbUp": false,
        "isThumbDown": false,
        "chatIssueReasons": reasons
      }

      localStorage.setItem("chatbot_info_storage", JSON.stringify({
        ...chatbotInfoStorage,
        messageList: [
          ...updatedMsgList,
          question,
          answer
        ]
      }));
      setChatbotInfoStorage({
        ...chatbotInfoStorage,
        messageList: [
          ...updatedMsgList,
          question,
          answer
        ]
      });

      resetChatbotChannel.postMessage({
        ...chatbotInfoStorage,
        messageList: [
          ...updatedMsgList,
          question,
          answer
        ]
      });
    });
  }

  const sendFeedbackReasons = (reasons) => {
    //console.log("sendFeedbackReasons()", reasons, message);

    // 1. call the api to save the feedback reasons
    // 2. set the message list
    saveChatFeedback({
      chatId: Number(message.chatId.split("_")[0]),
      isHelpful: 0,
      reasonIds: reasons
    }).then((res) => {
      var statusCode = res.data.status;
      if (statusCode < 0) {
        statusCode = STATUSES.ERROR.code;
      }
      switch (statusCode) {
        case STATUSES.ERROR.code:
          if (res.data.globalErrorMessageList && res.data.globalErrorMessageList.length != 0) {
            setGlobalErrorStatus({
              dispatch: dispatch,
              popupData: {
                visible: true,
                title: ERROR_MESSAGES.GENERAL_ERROR.title,
                msg: res.data.globalErrorMessageList,
                showContactHelp: false
              },
              redirect: {
                redirect: false,
                redirectFirst: false,
                path: "",
                data: {}
              }
            })
          };

          break;
        case STATUSES.SUCCESS.code:

          // set the reason message list
          let qlist = [];
          let rlist = [];
          let chatIssueReasons = [...message?.chatIssueReasons];

          for (let i = 0; i < chatIssueReasons.length; i++) {
            let r = chatIssueReasons[i];

            if (reasons.includes(r.reasonId)) {
              //console.log("find one item", r);
              rlist.push({
                ...r,
                checked: true
              });

              let question = {
                "message": r.reasonText,
                "chatId": null,
                "documents": [],
                "webpages": [],
                "reset_memory": false,
                "session_id": chatbotInfoStorage.sessionId,
                "timestamp": buildTimestamp()
              }

              qlist.push(question);
            } else {
              rlist.push({ ...r })
            }
          }

          //console.log("sendFeedbackReasons()", rlist, qlist);

          const updatedMsgList = [...chatbotInfoStorage.messageList].map(item =>
            item.chatId === message.chatId ? { ...item, chatIssueReasons: rlist, isChatIssueSubmit: true } : item
          );

          let answer = {
            "message": "Thank you for your feedback! 👋",
            "chatId": -1,
            "documents": [],
            "webpages": [],
            "reset_memory": false,
            "session_id": chatbotInfoStorage.sessionId,
            "timestamp": buildTimestamp()
          }

          //console.log("sendFeedbackReasons()", updatedMsgList);
          localStorage.setItem("chatbot_info_storage", JSON.stringify({
            ...chatbotInfoStorage,
            messageList: [
              ...updatedMsgList,
              ...qlist,
              answer
            ]
          }));
          setChatbotInfoStorage({
            ...chatbotInfoStorage,
            messageList: [
              ...updatedMsgList,
              ...qlist,
              answer
            ]
          });

          resetChatbotChannel.postMessage({
            ...chatbotInfoStorage,
            messageList: [
              ...updatedMsgList,
              ...qlist,
              answer
            ]
          });
          break;
      }
    }).catch((error) => {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });
  }

  return (
    <>
      {isLeft(message) ?
        <>
          <div className="d-flex chatbot-message-right mb-2">
            {/* message content */}
            <div className="me-1 chatbot-message-content">
              {message?.message}
            </div>
            {/* icon */}
            <div className="mt-auto">
              <AccountCircleIcon className="user-icon"></AccountCircleIcon>
            </div>
          </div>
          {/* message timestamp */}
          <div className="d-flex chatbot-message-timestamp-right mb-2">
            {message?.timestamp}
          </div>
        </>
        :
        <>
          <div className="d-flex chatbot-message-left mb-2">
            {/* logo */}
            <div className="mt-auto">
              <span className="chatbot-icon d-flex align-items-center justify-content-center">
                <Image
                  alt="PSOPPC Logo" src={Logo}
                  height={"16px"}
                  width={"16px"}
                />
              </span>
            </div>
            {/* message content */}
            <div className="ms-2 chatbot-message-content">
              {/* text */}
              {message?.message === "No answer found" ?
                <>
                  <div>
                    I’m sorry, I couldn’t find an answer to that. You can:
                  </div>
                  <ul className='p-0 ps-3'>
                    <li className='mt-1'><strong>Rephrase</strong> your question with different keywords.</li>
                    <li className='mt-1'>Visit our <HashLink
                      to={`${PATHS.public['Get Help']}`}
                    >
                      Get Help
                    </HashLink> page for guides and FAQs. </li>
                    <li className='mt-1'>Contact <a href="mailto:support@psoppc.org">support@psoppc.org</a>  for direct assistance. </li>
                  </ul>
                </>
                : <Markdown rehypePlugins={[rehypeRaw]}>{message?.message}</Markdown>
              }
              {message.webpages.length > 0 || message.documents.length > 0 ?
                <div
                  className='mt-2'
                >
                  For detailed information, please refer to the following sources:
                </div> : null
              }
              {/*webpage link */}
              {message?.webpages.map((item, index) => {
                return (
                  <div>
                    <Button
                      key={item.description}
                      className="m-0 p-0 mt-2 chatbot-message-additional-info text-start"
                      onClick={() => handleNavigate(item.url)}
                    >
                      {item.description}
                    </Button>
                  </div>
                )
              })}
              {/* doc link */}
              {message.documents.map((item, index) => {
                return <ChatDocument key={item.doc_name} item={item} />
              })}
            </div>
          </div>
          {/* message timestamp */}
          <div className="chatbot-message-timestamp-left mb-2">
            {message?.timestamp}
          </div>
          {/* was this helpful */}
          {isFeedback(message) ?
            <div className="d-flex align-items-center chatbot-message-feedback mb-2">
              Was this helpful?
              &nbsp;
              {message?.isThumbUp ?
                <Button
                  aria-label="thumb up"
                >
                  <ThumbUpAltIcon />
                </Button> :
                <Button
                  aria-label="thumb up"
                  onClick={() => handleThumbUp(message)}
                  onKeyDown={(e) => handleThumbUpByKey(e, message)}
                >
                  <ThumbUpOffAltIcon />
                </Button>}
              &nbsp;
              {message?.isThumbDown ?
                <Button
                  aria-label="thumb down"
                >
                  <ThumbDownAltIcon />
                </Button> :
                <Button
                  aria-label="thumb down"
                  onClick={() => handleThumbDown(message)}
                  onKeyDown={(e) => handleThumbDownByKey(e, message)}
                >
                  <ThumbDownOffAltIcon />
                </Button>}
            </div>
            : null}
          {isChatIssueReason(message) ?
            <div className='chatbot-message-feedback-form'>
              {message.chatIssueReasons.map((item, index) => (
                <ChatIssueReason
                  reason={item}
                  key={"chat_reason_" + index}
                  feedbackReasons={feedbackReasons}
                  setFeedbackReasons={setFeedbackReasons}
                  message={message}
                >
                </ChatIssueReason>
              ))}
              <TextButton variant="normal"
                content="Send"
                onClick={() => sendFeedbackReasons(feedbackReasons)}
                className="m-0 p-0 ps-3 pe-3 pt-1 pb-1"
                disabled={message?.isChatIssueSubmit === true}
              />
            </div>
            : null
          }
        </>}
    </>
  )
}

export default ChatMessage;